import { useMemo } from 'react'

/**
 * 零依赖纯 TypeScript 极简 QR Code 矩阵生成器
 * 支持字节编码模式与常见链接/口令生成
 */

// Galois Field GF(256) tables for Reed-Solomon encoding
const EXP_TABLE = new Uint8Array(512)
const LOG_TABLE = new Uint8Array(256)
;(() => {
  let x = 1
  for (let i = 0; i < 255; i++) {
    EXP_TABLE[i] = x
    EXP_TABLE[i + 255] = x
    LOG_TABLE[x] = i
    x = (x << 1) ^ (x >= 128 ? 0x11d : 0)
  }
})()

function gmult(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return EXP_TABLE[LOG_TABLE[a] + LOG_TABLE[b]]
}

function polyMultiply(p1: number[], p2: number[]): number[] {
  const result = new Array(p1.length + p2.length - 1).fill(0)
  for (let i = 0; i < p1.length; i++) {
    for (let j = 0; j < p2.length; j++) {
      result[i + j] ^= gmult(p1[i], p2[j])
    }
  }
  return result
}

function getGeneratorPoly(degree: number): number[] {
  let poly = [1]
  for (let i = 0; i < degree; i++) {
    poly = polyMultiply(poly, [1, EXP_TABLE[i]])
  }
  return poly
}

function calculateECC(data: number[], eccLength: number): number[] {
  const gen = getGeneratorPoly(eccLength)
  const remainder = [...data, ...new Array(eccLength).fill(0)]
  for (let i = 0; i < data.length; i++) {
    const factor = remainder[i]
    if (factor !== 0) {
      for (let j = 0; j < gen.length; j++) {
        remainder[i + j] ^= gmult(gen[j], factor)
      }
    }
  }
  return remainder.slice(data.length)
}

// 简易 QR 矩阵渲染器（根据文本长度自适应版本 1~6）
function generateQrMatrix(text: string): boolean[][] {
  const utf8Bytes = Array.from(new TextEncoder().encode(text))
  const length = utf8Bytes.length

  // 根据数据量选择合适版本 (Version 1: 17, V2: 32, V3: 53, V4: 78, V5: 106, V6: 134)
  const capacityTable = [
    { version: 1, size: 21, dataCap: 17, eccCap: 7 },
    { version: 2, size: 25, dataCap: 32, eccCap: 10 },
    { version: 3, size: 29, dataCap: 53, eccCap: 15 },
    { version: 4, size: 33, dataCap: 78, eccCap: 20 },
    { version: 5, size: 37, dataCap: 106, eccCap: 26 },
    { version: 6, size: 41, dataCap: 134, eccCap: 36 },
  ]

  const cfg = capacityTable.find((c) => c.dataCap >= length + 3) || capacityTable[capacityTable.length - 1]
  const { size, dataCap, eccCap } = cfg

  // 数据编码：Byte 模式 (0100) + 字符计数 + 数据 + 终止符
  const bitStream: number[] = []
  const pushBits = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) {
      bitStream.push((val >> i) & 1)
    }
  }

  pushBits(0b0100, 4) // 8-bit byte mode
  pushBits(length, 8)
  for (const b of utf8Bytes) {
    pushBits(b, 8)
  }
  pushBits(0, 4) // terminator

  while (bitStream.length % 8 !== 0) {
    bitStream.push(0)
  }

  const dataBytes: number[] = []
  for (let i = 0; i < bitStream.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) {
      byte = (byte << 1) | (bitStream[i + j] || 0)
    }
    dataBytes.push(byte)
  }

  // 补齐填充码
  const padBytes = [0xec, 0x11]
  let padIdx = 0
  while (dataBytes.length < dataCap) {
    dataBytes.push(padBytes[padIdx % 2])
    padIdx++
  }

  // 计算纠错码
  const eccBytes = calculateECC(dataBytes, eccCap)
  const finalBytes = [...dataBytes, ...eccBytes]

  // 初始化矩阵
  const matrix: boolean[][] = Array.from({ length: size }, () => new Array(size).fill(false))
  const isFunctionPattern: boolean[][] = Array.from({ length: size }, () => new Array(size).fill(false))

  // 绘制 Finder 寻像图案
  const drawFinder = (startX: number, startY: number) => {
    for (let y = -1; y <= 7; y++) {
      for (let x = -1; x <= 7; x++) {
        const px = startX + x
        const py = startY + y
        if (px >= 0 && px < size && py >= 0 && py < size) {
          isFunctionPattern[py][px] = true
          if (
            (x >= 0 && x <= 6 && (y === 0 || y === 6)) ||
            (y >= 0 && y <= 6 && (x === 0 || x === 6)) ||
            (x >= 2 && x <= 4 && y >= 2 && y <= 4)
          ) {
            matrix[py][px] = true
          } else {
            matrix[py][px] = false
          }
        }
      }
    }
  }

  drawFinder(0, 0)
  drawFinder(size - 7, 0)
  drawFinder(0, size - 7)

  // 绘制 Timing 同步图案
  for (let i = 8; i < size - 8; i++) {
    isFunctionPattern[6][i] = true
    isFunctionPattern[i][6] = true
    matrix[6][i] = i % 2 === 0
    matrix[i][6] = i % 2 === 0
  }

  // 暗点
  matrix[size - 8][8] = true
  isFunctionPattern[size - 8][8] = true

  // 掩模与数据填入 (Mask pattern 0: (row + col) % 2 === 0)
  let byteIndex = 0
  let bitIndex = 7
  let upward = true

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col--
    const rows = upward
      ? Array.from({ length: size }, (_, i) => size - 1 - i)
      : Array.from({ length: size }, (_, i) => i)

    for (const row of rows) {
      for (const c of [col, col - 1]) {
        if (!isFunctionPattern[row][c]) {
          let bit = 0
          if (byteIndex < finalBytes.length) {
            bit = (finalBytes[byteIndex] >> bitIndex) & 1
            bitIndex--
            if (bitIndex < 0) {
              bitIndex = 7
              byteIndex++
            }
          }
          // Apply mask 0: invert if (row + col) % 2 === 0
          const mask = (row + c) % 2 === 0
          matrix[row][c] = (bit === 1) !== mask
        }
      }
    }
    upward = !upward
  }

  return matrix
}

export interface QRCodeProps {
  value: string
  size?: number
  className?: string
  color?: string
  bgColor?: string
}

export function QRCode({
  value,
  size = 180,
  className = '',
  color = '#111827',
  bgColor = '#ffffff',
}: QRCodeProps) {
  const matrix = useMemo(() => {
    try {
      return generateQrMatrix(value || 'https://easy-share')
    } catch (e) {
      console.warn('QR Code generation fallback:', e)
      return []
    }
  }, [value])

  if (!matrix.length) {
    return <div style={{ width: size, height: size }} className="bg-gray-100 rounded-lg" />
  }

  const matrixSize = matrix.length
  const quietZone = 2
  const totalGrid = matrixSize + quietZone * 2

  let pathData = ''
  for (let r = 0; r < matrixSize; r++) {
    for (let c = 0; c < matrixSize; c++) {
      if (matrix[r][c]) {
        pathData += `M${c + quietZone},${r + quietZone}h1v1h-1z `
      }
    }
  }

  return (
    <div
      className={`inline-flex items-center justify-center p-2 rounded-xl border border-gray-100 shadow-sm ${className}`}
      style={{ backgroundColor: bgColor }}
    >
      <svg
        viewBox={`0 0 ${totalGrid} ${totalGrid}`}
        width={size}
        height={size}
        shapeRendering="crispEdges"
      >
        <rect width={totalGrid} height={totalGrid} fill={bgColor} />
        <path d={pathData} fill={color} />
      </svg>
    </div>
  )
}
